import { TranslationMessages } from "ra-core";

import { germanMessages } from "./germanMessages";

export const englishMessages = {
  integration: {
    // The locale merge is shallow, so keep the German fallbacks of the other keys.
    ...(germanMessages.integration as TranslationMessages),
    connectionTest: {
      saveFirst: "Save your changes to test the connection.",
      success:
        "Connection successful: read %{smart_count} site (%{seconds} s). |||| Connection successful: read %{smart_count} sites (%{seconds} s).",
      failed: "Connection failed %{stage}: %{reason}",
      stage: {
        token: "while signing in",
        read: "while reading data",
      },
      reason: {
        not_configured: "No complete credentials are saved.",
        unsupported: "There is no connection test for this provider yet.",
        timeout: "The server did not respond in time. A firewall often blocks our IP address.",
        unreachable: "The server cannot be reached (unknown address or connection refused).",
        tls: "The server's TLS certificate is invalid or does not match the address.",
        credentials: "The credentials were rejected (HTTP %{status}).",
        http_error: "The server responded with HTTP %{status}.",
        invalid_response: "The server returned an unexpected response.",
        unknown: "Unknown error.",
      },
    },
    preview: {
      title: "Import preview",
      description: "Shows what the next import from SENSO would create or change. No data is written.",
      saveFirst: "Save your changes to create a preview.",
      loading: "Loading preview...",
      empty: "No preview yet. Create a preview to review the changes before importing.",
      running: "The preview is being created. This can take a moment.",
      createdAt: "Created %{date}",
      expiresAt: "valid until %{date}",
      procedureCount: "%{smart_count} procedure read |||| %{smart_count} procedures read",
      importNotice: "The import reads SENSO again, so the result may differ from this preview.",
      warnings: "Warnings",
      failures: "Failures",
      issueReference: "%{entity} %{reference}",
      button: {
        create: "Create preview",
        creating: "Creating preview...",
        import: "Import now",
      },
      error: {
        loading: "The preview could not be loaded",
        request: "The preview could not be created",
        failed: "The preview could not be created. Please try again.",
        cancelled: "The preview was cancelled. Please create it again.",
        reason: "Reason: %{reason}",
      },
      requests: {
        title: "SENSO requests",
        unknownPath: "(path unknown)",
        http: "HTTP %{status}",
        httpWithCodes: "HTTP %{status} (%{codes})",
        records: "%{smart_count} record |||| %{smart_count} records",
        duration: "%{seconds} s",
        timeout: "Timeout",
        networkError: "Network error (%{code})",
        failed: "Request failed",
      },
      rawData: {
        button: "Raw data",
        title: "Raw data from SENSO: %{endpoint}",
        description:
          "Fetches SENSO's response live and shows it as it came. The data is not stored and disappears when you close this window.",
        haus: "House",
        bewohner: "Resident",
        noHaus: "No house",
        noOptions: "The stored preview has no matching entries. Create a preview first to pick the parameters.",
        load: "Load",
        loading: "Loading raw data...",
        failed: "The raw data could not be loaded.",
        request: "Request",
        query: "Parameters",
        headers: "Headers",
        response: "Response",
        status: "HTTP %{status}",
        noBody: "The response has no content.",
        bodyBase64: "Not valid text in its declared character set; shown as Base64.",
        truncated: "Truncated, %{bytes} bytes total",
        error: {
          not_configured: "No complete credentials are saved.",
          unsupported: "There is no raw data for this provider.",
          credentials: "The credentials were rejected.",
          timeout: "The server did not respond in time.",
          unreachable: "The server cannot be reached (unknown address or connection refused).",
          tls: "The server's TLS certificate is invalid or does not match the address.",
          http_error: "The server responded with an error.",
          invalid_response: "The server returned an unexpected response.",
          too_large: "The response is too large to show.",
          unknown: "Unknown error.",
        },
      },
      totals: {
        title: "Overview",
        locations: "Houses/sites",
        patients: "Residents",
        practitioners: "Staff",
      },
      change: {
        new: "New",
        changed: "Changed",
        unchanged: "Unchanged",
        failed: "Failed",
      },
      tree: {
        title: "Structure",
        empty: "No sites in the preview.",
        residents: "%{smart_count} resident |||| %{smart_count} residents",
        unassigned:
          "Without a room assignment: %{smart_count} resident |||| Without a room assignment: %{smart_count} residents",
        unassignedHint: "New residents go to the organization; existing residents keep their current place.",
      },
      locationType: {
        ORGANIZATION: "Organization",
        SITE: "House",
        DEPARTMENT: "Department",
        WARD: "Ward",
        ROOM: "Room",
        BED: "Bed",
      },
      entity: {
        location: "Site",
        patient: "Resident",
        practitioner: "Staff member",
        procedure: "Procedure",
      },
      reason: {
        missingPatientName: "The resident's name is missing.",
        invalidBirthDate: "The birth date is invalid.",
        noRootLocation: "The organization has no root location.",
      },
    },
  },
  logisticsShippingLabel: {
    title: "Shipping labels",
    loading: "Loading label…",
    noOrder: "No local logistics order is available.",
    disabled: "Label actions are not enabled for your current access.",
    loadError: "The current label state could not be loaded. Refresh before continuing.",
    create: "Create label",
    cancel: "Cancel label",
    cancelling: "Cancelling label…",
    retryTracking: "Retry tracking update",
    cancelMarker:
      "Cancellation leaves an existing Salesforce export marker unchanged. That marker blocks a replacement pilot label.",
    creating: "Creating label…",
    retry: "Retry label publication",
    retryCreate: "Try creating the label again",
    continue: "Continue the same request",
    returnLabel: "Request an optional return label",
    refresh: "Refresh label state",
    transportLost:
      "The response was lost. Check the state or continue the same request; do not start a second shipment.",
    statusRefreshError: "The label result is saved, but the order update status could not be refreshed.",
    unknown: "The result is uncertain. Have this shipment checked before starting another action.",
    anotherPending: "Another label command is still being processed.",
    cancelledHistory: "Cancelled label — these PDFs are retained for history and must not be used for shipping.",
    outboundTracking: "Outbound tracking: %{number}",
    returnTracking: "Return tracking: %{number}",
    openOutbound: "Open / print outbound PDF",
    downloadOutbound: "Download outbound PDF",
    openReturn: "Open / print return PDF",
    downloadReturn: "Download return PDF",
    pdfError: "This PDF could not be opened. Refresh the label state.",
    pilotStatus: "Command status:",
    salesforceStatus: "Salesforce status:",
    refusalFallback: "The command was refused. Reload and check the order before trying again.",
    outcomes: {
      CREATED: "DHL label created and stored.",
      EXISTING: "Using the stored DHL label.",
      CANCELLED: "DHL cancellation is recorded.",
      REFUSED: "DHL was not called for this command.",
      REJECTED: "DHL rejected the command.",
      FAILED: "DHL did not provide a usable answer.",
      UNKNOWN: "The label result is not confirmed yet.",
    },
    phases: {
      IN_PROGRESS: "Processing…",
      SUCCEEDED: "Completed.",
      REFUSED: "Refused.",
      REJECTED: "Rejected.",
      UNKNOWN: "Uncertain; check before continuing.",
    },
    address: {
      RECIPIENT: "Complete the recipient.",
      STREET: "Complete the street.",
      POSTAL_CODE: "Complete the postal code.",
      CITY: "Complete the city.",
      HOUSE_NUMBER: "Complete the house number.",
    },
    failures: {
      UNREACHABLE: "DHL could not be reached.",
      UNAUTHORIZED: "DHL rejected the configured credentials.",
      RATE_LIMITED: "DHL requested a slower request rate.",
      PROVIDER_ERROR: "DHL returned a provider error.",
      INVALID_RESPONSE: "DHL returned an unreadable answer.",
    },
    refusals: {
      NOT_ENABLED: "Labels are not enabled.",
      NOT_CONFIGURED: "The DHL settings are incomplete.",
      DHL_NOT_CONFIGURED: "The DHL settings are incomplete.",
      SALESFORCE_NOT_CONFIGURED: "The Salesforce connection is not configured.",
      ORDER_NOT_FOUND: "The order no longer exists.",
      NOT_READY_FOR_SHIPPING: "The order must be Ready for Shipping.",
      NO_LABEL_FOR_RECORD_TYPE: "This order type does not receive an outbound label.",
      COUNTRY_NOT_SUPPORTED: "This shipping country is not supported.",
      ADDRESS_INCOMPLETE: "Complete the shipping address on the order.",
      SALESFORCE_LABEL_REQUESTED: "Salesforce has already requested a label.",
      SALESFORCE_ALREADY_EXPORTED: "The order already has a Salesforce export marker.",
      SALESFORCE_FIELDS_UNAVAILABLE: "Required Salesforce fields could not be read.",
      SHIPPING_PLAN_CHANGED: "The shipping address or label plan changed. Check the existing label.",
      LABEL_NOT_PILOT: "This label does not belong to the configured pilot.",
      TRACKING_CHANGED: "Salesforce holds different tracking numbers. Reload and check before continuing.",
      LABEL_CHANGED: "The active label changed. Reload before cancelling.",
      LABEL_NOT_FOUND: "The selected label no longer exists.",
    },
  },
  logisticsCommandStatus: {
    loadError: "The order's update status could not be loaded.",
    refusalFallback: "Check the order before trying again.",
    states: {
      UPDATING: "The order is being updated from Salesforce…",
      SYNCED: "The order has been updated from Salesforce.",
      REJECTED: "Salesforce rejected the change.",
      REFUSED: "The change was not executed.",
      UNKNOWN: "The result of the change is uncertain. Have it checked before trying again.",
    },
    refusals: {
      PRECONDITION_CHANGED: "The order has changed in Salesforce. Reload and check it before trying again.",
      PILOT_DISABLED: "The logistics pilot is disabled.",
      PILOT_NOT_ALLOWED: "You are not a participant in the logistics pilot.",
      PERMISSION_DENIED: "You do not have permission to make this change.",
      NOT_PILOT: "This change is not available through the logistics pilot.",
      NOT_CONFIGURED: "The Salesforce connection is not configured.",
      CONFIGURATION_CHANGED: "The Salesforce connection changed. Reload the order before trying again.",
      COMMAND_IN_PROGRESS: "Another change to this order or its records is still being processed or checked.",
      IDEMPOTENCY_CONFLICT: "This request was already used for a different change. Reload the order.",
      SALESFORCE_UNAVAILABLE: "Salesforce could not be reached. Check the order before trying again.",
      INVALID_INPUT: "The change could not be submitted. Reload the order and check your entries.",
    },
  },
  dhlSettings: {
    checkConnection: "Check connection",
    checkingConnection: "Checking connection…",
    checkStoredSettings: "Checks the saved DHL credentials and loads Austrian return receivers.",
    saveBeforeCheck: "Save your changes before checking the connection. Only saved credentials are checked.",
    connectionSuccess: "Connected to DHL.",
    connectionError: "The DHL connection could not be checked. Please try again.",
    unlistedReceiverOption: "%{receiverId} (saved, not in the DHL list)",
    unlistedReceiver:
      "The saved return receiver is not in the DHL list. Select a listed Austrian receiver and save it.",
    noReceivers:
      "DHL returned no Austrian return receivers. Configure an Austrian return receiver with DHL, then check again.",
    connectionFailures: {
      NOT_CONFIGURED:
        "Saved DHL credentials are missing or unavailable. Enter and save the missing credentials, then check again.",
      UNAUTHORIZED:
        "DHL rejected the saved credentials. Check and save your client ID, client secret, username and password, then try again.",
      UNREACHABLE: "DHL could not be reached. Check your connection and try again later.",
      RATE_LIMITED: "DHL is receiving too many requests. Wait a moment, then try again.",
      PROVIDER_ERROR: "DHL could not complete the check. Please try again later.",
      INVALID_RESPONSE:
        "DHL returned an unreadable response. Try again later; contact support if the problem persists.",
    },
    accountTitle: "DHL account",
    accountDescription: "Configure the saved DHL credentials, billing numbers and Austrian return receiver.",
    gateway: "DHL gateway",
    gateways: { SANDBOX: "Sandbox", PRODUCTION: "Production" },
    secretStored: "Stored. Leave blank to keep the existing value.",
    secretRequired: "Required for the first save. Please enter a value.",
    secretRequiredAgain: "Enter again after changing the associated client ID or username.",
    title: "DHL settings",
    shippingTitle: "Shipping settings",
    shippingDescription: "Configure the sender, return address and print formats for DHL shipping labels.",
    shipper: "Sender",
    returnAddress: "Return address (optional)",
    returnHint: "Fill in all four fields or leave all empty. Without a return address, DHL uses the sender.",
    missingSettings: "Shipping labels still need the following settings:",
    loading: "Loading DHL settings",
    loadError: "DHL settings could not be loaded.",
    saved: "DHL settings saved.",
    saveError: "DHL settings could not be saved.",
    invalid: "Please check the marked fields.",
    fieldInvalid: "Please enter a valid value.",
    save: "Save",
    saving: "Saving…",
    settings: {
      CLIENT_ID: "Client ID",
      CLIENT_SECRET: "Client secret",
      USERNAME: "Username",
      PASSWORD: "Password",
      BILLING_NUMBER_PAKET: "Paket billing number (Germany)",
      BILLING_NUMBER_PAKET_INTERNATIONAL: "Paket billing number (international)",
      BILLING_NUMBER_RETOURE: "Retoure billing number",
      RETURN_RECEIVER_ID_AT: "Austrian return receiver ID",
      SHIPPER_NAME: "Sender name",
      SHIPPER_STREET: "Sender street and house number",
      SHIPPER_POSTAL_CODE: "Sender postal code",
      SHIPPER_CITY: "Sender city",
      RETURN_NAME: "Return name",
      RETURN_STREET: "Return street and house number",
      RETURN_POSTAL_CODE: "Return postal code",
      RETURN_CITY: "Return city",
      LABEL_PRINT_FORMAT: "Shipping label print format",
      RETURN_LABEL_PRINT_FORMAT: "Austrian return label print format",
    },
  },
};
